import IconButton, { type IconButtonProps } from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import { type ReactElement } from 'react';

import { whatsapp } from '@/theme/tokens';

export type MediaIconTone = 'neutral' | 'active' | 'whatsapp';

type Props = Omit<IconButtonProps, 'color' | 'children' | 'size'> & {
  /** The accessible name, also shown as the tooltip. */
  label: string;
  icon: ReactElement;
  tone?: MediaIconTone;
  /** Renders the control as an anchor instead of a button. */
  href?: string;
  target?: string;
  rel?: string;
};

/**
 * A round action that sits on top of a photograph (§5). It carries its own
 * opaque disc rather than relying on the picture behind it, because a white
 * glyph on an unknown photograph has no guaranteed contrast.
 *
 * The label is never rendered as text — these are the secondary actions on a
 * card whose one written call to action is "View Details" — so it is supplied
 * to both the tooltip and the accessible name.
 */
export function MediaIconButton({ label, icon, tone = 'neutral', sx, ...rest }: Props) {
  return (
    <Tooltip title={label} disableInteractive>
      <span style={{ display: 'inline-flex' }}>
        <IconButton
          aria-label={label}
          size="small"
          sx={[
            (theme) => ({
              width: 40,
              height: 40,
              minWidth: 40,
              minHeight: 40,
              borderRadius: '50%',
              border: '1px solid',
              borderColor: 'rgba(11, 11, 12, 0.08)',
              backgroundColor: 'rgba(255, 255, 255, 0.94)',
              color: 'text.primary',
              backdropFilter: 'blur(6px)',
              boxShadow: '0 2px 10px rgba(11, 11, 12, 0.18)',
              transition: 'background-color 200ms ease, color 200ms ease, transform 200ms ease',
              '&:hover': {
                backgroundColor: 'common.white',
                transform: 'scale(1.06)',
              },
              '& .MuiSvgIcon-root': { fontSize: 20 },
              '@media (prefers-reduced-motion: reduce)': {
                transition: 'none',
                '&:hover': { transform: 'none' },
              },
              ...theme.applyStyles('dark', {
                borderColor: 'rgba(255, 255, 255, 0.14)',
                backgroundColor: 'rgba(26, 24, 25, 0.92)',
              }),
            }),
            tone === 'active' &&
              ((theme) => ({
                backgroundColor: theme.vars.palette.primary.main,
                color: theme.vars.palette.primary.contrastText,
                borderColor: 'transparent',
                '&:hover': {
                  backgroundColor: theme.vars.palette.primary.dark,
                  transform: 'scale(1.06)',
                },
              })),
            tone === 'whatsapp' && {
              backgroundColor: whatsapp.main,
              color: whatsapp.onMain,
              borderColor: 'transparent',
              '&:hover': { backgroundColor: whatsapp.hover, transform: 'scale(1.06)' },
            },
            ...(Array.isArray(sx) ? sx : [sx]),
          ]}
          {...rest}
        >
          {icon}
        </IconButton>
      </span>
    </Tooltip>
  );
}
